import { useCallback, useState, type ReactElement, type ReactNode } from 'react'
import {
  DEFAULT_PREFERENCES,
  Logger,
  metersToMiles,
  milesToMeters,
  type EntityId,
  type PreferenceGetSuccess,
  type UnitSystemPreference,
} from '@terros/common'
import { notifications } from '@mantine/notifications'
import { useDebouncedCallback } from '@mantine/hooks'
import { NumberInput, type NumberInputProps } from '@mantine/core'
import { usePreference, useUpdatePreference } from '@front/model'
import { PreferenceSkeleton } from './PreferenceSkeleton'
import { PreferenceInput } from './PreferenceInput'
import { usePreferenceContext } from './Context'

const logger = new Logger('PreferenceMetersInput')
logger.level = 'verbose'

type MetersKeys =
  | 'maxMetersFromEventToDisposition'
  | 'metersBetweenPitchesToday'
  | 'maxMetersFromLocationToActivity'
  | 'maxMetersFromLocationToPitch'
  | 'conversationLocationUpdateThreshold'

type Props = Pick<NumberInputProps, 'min' | 'max' | 'disabled'> & {
  preference: MetersKeys
  targetId: EntityId
}

export const PreferenceMetersInput = ({ preference, targetId, disabled, ...props }: Props): ReactElement => {
  const { data: unitSystem, isLoading } = usePreference('unitSystem', targetId)
  if (isLoading) return <PreferenceSkeleton />
  return (
    <PreferenceInput targetId={targetId} preference={preference}>
      {({ data }) => (
        <NumberInputField
          unitSystem={unitSystem}
          data={data}
          disabled={disabled}
          preference={preference}
          targetId={targetId}
          {...props}
        />
      )}
    </PreferenceInput>
  )
}

type MetersInputFieldProps<T extends MetersKeys> = {
  data: PreferenceGetSuccess<T>
  unitSystem: UnitSystemPreference | undefined
} & Props
const NumberInputField = <T extends MetersKeys>({
  data,
  disabled,
  preference,
  targetId,
  unitSystem = DEFAULT_PREFERENCES.unitSystem,
  min,
  max,
  ...props
}: MetersInputFieldProps<T>): ReactNode => {
  const { isAllowedUpdate } = usePreferenceContext()
  const startValue = data.current ?? data.inherited
  const isImperial = unitSystem === 'imperial'
  const savedValue = isImperial ? metersToMiles(startValue) : startValue
  const [inputValue, setInputValue] = useState<string | number>(savedValue)
  const { update } = useUpdatePreference(preference, targetId)
  const debouncedUpdate = useDebouncedCallback(async (value: number) => {
    const metersValue = isImperial ? milesToMeters(value) : value
    logger.verbose('updating', { isImperial, metersValue, value })
    await update(metersValue)
    notifications.show({ message: 'Preference saved', color: 'green' })
  }, 1000)

  const handleChange = useCallback(
    (newValue: string | number) => {
      setInputValue(newValue)

      if (typeof newValue === 'string') {
        debouncedUpdate.cancel()
        return
      }

      debouncedUpdate(newValue)
    },
    [debouncedUpdate]
  )

  const handleBlur = useCallback(() => {
    setInputValue((value) => (typeof value === 'string' ? savedValue : value))
  }, [savedValue])

  const suffix = isImperial ? ' miles' : ' meters'
  const defaultValue = DEFAULT_PREFERENCES[preference]

  const resolvedMax = isImperial ? milesToMeters(max ?? Infinity) : max
  const resolvedMin = isImperial ? milesToMeters(min ?? 0) : min

  return (
    <NumberInput
      {...props}
      allowDecimal={isImperial}
      decimalScale={isImperial ? 2 : 0}
      max={resolvedMax}
      min={resolvedMin}
      placeholder={`${defaultValue}${suffix}`}
      suffix={suffix}
      disabled={disabled || !isAllowedUpdate}
      flex={1}
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  )
}
